import { logWithAmplitude } from './api';

export interface RaceShare {
    label: string;
    percent: number;
}

export interface University {
    name: string;
    enrollment: number;
}

export interface AreaDemographics {
    countyName: string;
    stateCode: string;
    year: number;
    totalPopulation: number;
    raceShares: RaceShare[];
    genderShares: RaceShare[];
    medianHouseholdIncome: number | undefined;
    medianAge: number | undefined;
    universities: University[];
    profileUrl: string;
}

// Institutions below this enrollment are dropped so the list shows only
// schools with a meaningful student population.
const MIN_UNIVERSITY_ENROLLMENT = 5000;
const MAX_UNIVERSITIES = 5;

interface DataUsaRow {
    [key: string]: string | number;
}

const DATA_USA_BASE = 'https://api.datausa.io/tesseract/data.jsonrecords';

const demographicsCache: Map<string, Promise<AreaDemographics>> = new Map();
const CACHE_LIMIT = 100;

async function fetchCountyForCoordinates(lat: number, lng: number): Promise<{ fips: string; countyName: string; stateCode: string }> {
    const response = await fetch(`https://geo.fcc.gov/api/census/area?lat=${lat}&lon=${lng}&format=json`);
    if (!response.ok) {
        throw new Error(`County lookup failed with status ${response.status}`);
    }
    const body = await response.json() as { results?: Array<{ county_fips?: string; county_name?: string; state_code?: string }> };
    const result = body?.results?.[0];
    if (result?.county_fips === undefined || result.county_fips === '') {
        throw new Error('No county found for coordinates');
    }
    return { fips: result.county_fips, countyName: result.county_name ?? 'Unknown County', stateCode: result.state_code ?? '' };
}

async function fetchDataUsaRows(cube: string, drilldowns: string, measure: string, countyGeoid: string): Promise<DataUsaRow[]> {
    const url = `${DATA_USA_BASE}?cube=${cube}&drilldowns=${drilldowns}&measures=${encodeURIComponent(measure)}&include=County:${countyGeoid}&time=year.latest`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Demographics fetch failed with status ${response.status}`);
    }
    const body = await response.json();
    return body?.data ?? [];
}

// B03002: rows are Race x Ethnicity population counts. Hispanic (any race) is
// reported as one share; non-Hispanic rows keep their race group.
function computeRaceShares(rows: DataUsaRow[]): { shares: RaceShare[]; total: number } {
    let total = 0;
    let hispanic = 0;
    const nonHispanicByRace: Map<string, number> = new Map();
    for (const row of rows) {
        const population = Number(row['Hispanic Population'] ?? 0);
        total += population;
        if (row['Ethnicity ID'] === 1) {
            hispanic += population;
        } else {
            const race = String(row.Race ?? 'Other');
            nonHispanicByRace.set(race, (nonHispanicByRace.get(race) ?? 0) + population);
        }
    }
    if (total === 0) {
        return { shares: [], total: 0 };
    }
    const friendlyLabels: { [key: string]: string } = {
        'White Alone': 'White',
        'Black or African American Alone': 'Black or African American',
        'Asian Alone': 'Asian',
        'American Indian & Alaska Native Alone': 'Native American',
        'Native Hawaiian & Other Pacific Islander Alone': 'Pacific Islander',
    };
    let other = 0;
    const shares: RaceShare[] = [];
    for (const [race, population] of nonHispanicByRace) {
        const label = friendlyLabels[race];
        if (label === undefined) {
            other += population;
        } else {
            shares.push({ label, percent: (population / total) * 100 });
        }
    }
    shares.push({ label: 'Hispanic or Latino', percent: (hispanic / total) * 100 });
    if (other > 0) {
        shares.push({ label: 'Other or Multiracial', percent: (other / total) * 100 });
    }
    shares.sort((a, b) => b.percent - a.percent);
    return { shares, total };
}

export async function getDemographicsForCoordinates(amplitude_callsite: string, lat: number, lng: number): Promise<AreaDemographics> {
    const county = await fetchCountyForCoordinates(lat, lng);
    const cached = demographicsCache.get(county.fips);
    if (cached !== undefined) {
        return await cached;
    }
    const promise = (async () => {
        const countyGeoid = `05000US${county.fips}`;
        const [raceRows, incomeRows, ageRows, genderRows, universityRows] = await Promise.all([
            fetchDataUsaRows('acs_ygr_race_with_hispanic_5', 'Race,Ethnicity', 'Hispanic Population', countyGeoid),
            fetchDataUsaRows('acs_ygr_median_household_income_race_5', 'Race,Year', 'Household Income by Race', countyGeoid),
            fetchDataUsaRows('acs_ygs_median_age_total_5', 'Year', 'Median Age', countyGeoid),
            fetchDataUsaRows('acs_yghsa_health_coverage_status_by_gender_by_age_5', 'Gender', 'Health Insurance by Gender and Age', countyGeoid),
            fetchDataUsaRows('ipeds_enrollment', 'University', 'Enrollment', countyGeoid),
        ]);
        const { shares, total } = computeRaceShares(raceRows);
        if (shares.length === 0) {
            throw new Error('No demographic data available for this area');
        }
        const incomeRow = incomeRows.find((row) => row.Race === 'Total');
        const income = incomeRow !== undefined ? Number(incomeRow['Household Income by Race']) : undefined;
        const age = ageRows.length > 0 ? Number(ageRows[0]['Median Age']) : undefined;
        const year = Number(incomeRow?.Year ?? new Date().getFullYear());
        const genderTotal = genderRows.reduce((sum, row) => sum + Number(row['Health Insurance by Gender and Age'] ?? 0), 0);
        const genderShares: RaceShare[] = genderTotal > 0
            ? genderRows.map((row) => ({
                label: String(row.Gender ?? ''),
                percent: (Number(row['Health Insurance by Gender and Age'] ?? 0) / genderTotal) * 100,
            }))
            : [];
        const universities: University[] = universityRows
            .map((row) => ({ name: String(row.University ?? ''), enrollment: Number(row.Enrollment ?? 0) }))
            .filter((u) => u.name !== '' && u.enrollment >= MIN_UNIVERSITY_ENROLLMENT)
            .sort((a, b) => b.enrollment - a.enrollment)
            .slice(0, MAX_UNIVERSITIES);
        return {
            countyName: county.countyName,
            stateCode: county.stateCode,
            year,
            totalPopulation: total,
            raceShares: shares,
            genderShares,
            medianHouseholdIncome: Number.isFinite(income) ? income : undefined,
            medianAge: Number.isFinite(age) ? age : undefined,
            universities,
            profileUrl: `https://datausa.io/profile/geo/${countyGeoid}`,
        };
    })();
    const handledPromise = promise.catch((error) => {
        demographicsCache.delete(county.fips);
        throw error;
    });
    demographicsCache.set(county.fips, handledPromise);
    if (demographicsCache.size > CACHE_LIMIT) {
        const firstKey = demographicsCache.keys().next().value;
        demographicsCache.delete(firstKey);
    }
    const result = await handledPromise;
    logWithAmplitude(amplitude_callsite, 'Demographics Loaded');
    return result;
}
